import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRightLeft, Mic, MicOff, PhoneOff, ExternalLink, UserPlus } from 'lucide-react';
import { useVoiceAgentStore } from '@stores/voiceAgentStore';
import { TransferDialog } from './TransferDialog';
import { CallParticipants } from './CallParticipants';
import { callControls } from '../callConference';
import { outgoingCallLabel } from '../outboundCall';

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

/**
 * ActiveCallBar — minimal sticky bar at the top of the viewport during an active call.
 * Hidden when on the ActiveCallPage (which has its own full controls).
 */
export function ActiveCallBar() {
  const { activeCall, outgoingCall, callContext, isMuted, callDurationSeconds, mute, unmute, hangup, setCallDuration, participants, isGuest } =
    useVoiceAgentStore();
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [startTime] = useState(() => Date.now());
  const [dialog, setDialog] = useState<'transfer' | 'conference' | null>(null);
  const controls = callControls({ activeCall, callContext, participants, isGuest, outgoingCall });
  const location = useLocation();
  const answeredAt = outgoingCall?.answeredAt;
  const waiting = !!outgoingCall && !answeredAt;

  useEffect(() => {
    if (!activeCall || waiting) return;
    // A call this agent placed is timed from when the customer answered.
    const from = answeredAt ? Date.parse(answeredAt) : startTime;

    timerRef.current = setInterval(() => {
      setCallDuration(Math.max(0, Math.floor((Date.now() - from) / 1000)));
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [activeCall, waiting, answeredAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hide bar when on the active call page (it has its own controls)
  if (!activeCall || location.pathname === '/live-call/active') return null;

  return (
    <div className="fixed left-0 right-0 top-0 z-40 flex items-center justify-between bg-green-700 px-6 py-2 shadow-lg">
      {/* Caller info */}
      <div className="flex items-center gap-3">
        <span className="relative flex h-3 w-3">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-300 opacity-75" />
          <span className="relative inline-flex h-3 w-3 rounded-full bg-green-400" />
        </span>
        <p className="text-sm font-semibold text-white">
          {callContext?.customerPhone ?? 'Active call'}
        </p>
      </div>

      <div className="hidden md:block">
        <CallParticipants />
      </div>

      {/* Timer */}
      <span className="font-mono text-sm font-medium text-green-100">
        {waiting && outgoingCall ? outgoingCallLabel(outgoingCall) : formatDuration(callDurationSeconds)}
      </span>

      {/* Controls */}
      <div className="flex items-center gap-2">
        <button
          onClick={isMuted ? unmute : mute}
          title={isMuted ? 'Unmute' : 'Mute'}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 dark:bg-gray-800"
        >
          {isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </button>

        {controls.canTransfer && (
          <button
            type="button"
            onClick={() => setDialog('transfer')}
            title="Transfer to a colleague"
            aria-label="Transfer to a colleague"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 dark:bg-gray-800"
          >
            <ArrowRightLeft className="h-4 w-4" />
          </button>
        )}

        {controls.canAddColleague && (
          <button
            type="button"
            onClick={() => setDialog('conference')}
            title="Merge: add a colleague to the call"
            aria-label="Add a colleague to the call"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 dark:bg-gray-800"
          >
            <UserPlus className="h-4 w-4" />
          </button>
        )}

        <button
          onClick={hangup}
          disabled={!controls.canEnd}
          title={controls.canEnd ? controls.leaveLabel : "A ringing call can't be cancelled. It stops within 30 seconds."}
          aria-label={controls.leaveLabel}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-red-500 text-white transition hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <PhoneOff className="h-4 w-4" />
        </button>

        <Link
          to="/live-call/active"
          className="ml-2 flex items-center gap-1 text-xs text-green-200 transition hover:text-white"
        >
          <ExternalLink className="h-3 w-3" />
          View details
        </Link>
      </div>
      {dialog && <TransferDialog mode={dialog} onClose={() => setDialog(null)} />}
    </div>
  );
}